import { onBeforeMount } from 'vue';
import { createRouter, createWebHistory, useRouter } from 'vue-router';
import { useUserStore } from '@/store/userStore';

import LoginPage from '@/pages/authentication/LoginPage.vue';
import RegisterPage from '@/pages/authentication/RegisterPage.vue';
import ResetPasswordPage from '@/pages/authentication/ResetPasswordPage.vue';
import AcceptInvitePage from '@/pages/authentication/AcceptInvitePage.vue';

import FormEditorPage from '@/pages/flow/FormEditorPage.vue';
import FormViewerPage from '@/pages/public/FormViewerPage.vue';

import EditView from '@/pages/flow/EditView.vue';
import FormIntegrationsView from '@/pages/flow/FormIntegrationsView.vue';
import PerformanceView from '@/pages/flow/PerformanceView.vue';
import SubmissionView from '@/pages/flow/SubmissionView.vue';
import PublishView from '@/pages/flow/PublishView.vue';
import SettingsView from '@/pages/flow/SettingsView.vue';
import CPQSettingsView from '@/pages/flow/CPQSettingsView.vue';

import DefaultLayout from '@/layouts/DefaultLayout.vue';

import AllFormsPage from '@/pages/dashboard/AllFormsPage.vue';
import HomePage from '@/pages/dashboard/HomePage.vue';
import InsightsPage from '@/pages/dashboard/InsightsPage.vue';
import StatsPage from '@/pages/dashboard/StatsPage.vue';
import AutomationsView from '@/pages/dashboard/AutomationsPage.vue';
import AutomationEditor from '@/pages/dashboard/AutomationEditor.vue';
import CrmPage from '@/pages/dashboard/CrmPage.vue';
import LeadDetailPage from '@/pages/dashboard/LeadDetailPage.vue';
import DealsPage from '@/pages/dashboard/DealsPage.vue';
import TasksPage from '@/pages/dashboard/TasksPage.vue';
import AppointmentsPage from '@/pages/dashboard/AppointmentsPage.vue';
import PlanningPage from '@/pages/dashboard/PlanningPage.vue';
import QuotesPage from '@/pages/dashboard/QuotesPage.vue';
import AllSubmissionsPage from '@/pages/dashboard/AllSubmissionsPage.vue';
import ProductDetailPage from '@/pages/dashboard/ProductDetailPage.vue';
import ProductFeedDetailPage from '@/pages/dashboard/ProductFeedDetailPage.vue';
import { checkAuth } from '@/middleware/authMiddleware';

import SettingsLayout from '@/layouts/SettingsLayout.vue';
import SettingsIntegrationsView from '@/pages/settings/SettingsIntegrationsView.vue';
import SettingsSuppliersView from '@/pages/settings/SettingsSuppliersView.vue';
import SettingsMyAccountView from '@/pages/settings/SettingsMyAccountView.vue';
import SettingsNotificationsView from '@/pages/settings/SettingsNotificationsView.vue';
import SettingsOrganizationView from '@/pages/settings/SettingsOrganizationView.vue';
import SettingsBillingView from '@/pages/settings/SettingsBillingView.vue';
import SettingsInvoicingView from '@/pages/settings/SettingsInvoicingView.vue';
import CalendarCallback from '@/components/oauth/CalendarCallback.vue';
import GoogleAuthCallback from '@/components/oauth/GoogleAuthCallback.vue';

import SupportPage from '@/pages/support/SupportPage.vue';
import PrivacyPolicy from '@/pages/privacy-policy/PrivacyPolicy.vue';
import ProductFeedsView from '@/components/products/ProductFeedsView.vue';
import FullscreenLayout from '@/layouts/FullscreenLayout.vue';
import OnboardingPage from '@/pages/onboarding/OnboardingPage.vue';
import { needsOnboardingIntake } from '@/utils/onboarding';

// Called from route guards, after checkAuth has loaded the session.
function defaultAppPath() {
  const role = useUserStore().currentOrganization?.role;
  if (role === 'installer') return '/vandaag';
  if (role === 'admin' || role === 'super_admin') return '/home';
  return window.innerWidth < 992 ? '/deals' : '/all-flows';
}

// Child of `/` with no component is dropped, so this runs after auth has loaded.
const RootRedirect = {
  name: 'RootRedirect',
  setup() {
    const router = useRouter();
    onBeforeMount(() => {
      router.replace(defaultAppPath());
    });
    return {};
  },
  render: () => null,
};

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPasswordPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/accept-invite',
    name: 'AcceptInvite',
    component: AcceptInvitePage,
    meta: { requiresAuth: false },
  },
  {
    path: '/onboarding',
    component: FullscreenLayout,
    meta: {
      requiresAuth: true,
      roles: ['admin', 'owner', 'super_admin'],
      skipOnboardingRedirect: true,
    },
    children: [
      {
        path: '',
        name: 'Onboarding',
        component: OnboardingPage,
      },
    ],
  },
  {
    path: '/flow/:id',
    component: FormEditorPage,
    children: [
      {
        path: '',
        name: 'Edit',
        component: EditView,
        beforeEnter: async (to, from, next) => {
          const userStore = useUserStore();

          // Ensure user is initialized first
          if (!userStore.isAuthenticated) {
            try {
              const initialized = await userStore.init();
              if (!initialized) {
                next('/login');
                return;
              }
            } catch (error) {
              console.error('Failed to initialize user:', error);
              next('/login');
              return;
            }
          }

          // Check if this is a configurator - redirect to configurator management
          try {
            const { formService } = await import('@/services/formService');
            const form = await formService.getFormById(to.params.id);

            if (form.type === 'configurator') {
              // Redirect to configurator management instead of form editor
              next(`/configurator/${to.params.id}`);
              return;
            }
          } catch (error) {
            console.error('Failed to check form type:', error);
            // Continue to normal flow if check fails
          }

          // Now we can safely check admin status
          const role = userStore.currentOrganization?.role;
          const isAdmin = role === 'admin' || role === 'super_admin';
          if (!isAdmin || window.innerWidth < 992) {
            next({ name: 'Submissions', params: { id: to.params.id } });
          } else {
            next();
          }
        },
      },
      {
        path: 'settings',
        name: 'Settings',
        component: SettingsView,
        meta: { requiresAuth: true, roles: ['admin'] },
        children: [
          {
            path: ':tab',
            name: 'SettingsTab',
            component: SettingsView,
            props: true,
          },
        ],
      },
      {
        path: 'settings/:tab',
        name: 'ConfiguratorSettings',
        component: SettingsView,
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'form-integrations',
        name: 'FormIntegrations',
        component: FormIntegrationsView,
        meta: { requiresAuth: true, roles: ['admin'] },
      },
      {
        path: 'performance',
        name: 'Performance',
        component: PerformanceView,
      },
      {
        path: 'submissions',
        name: 'Submissions',
        component: SubmissionView,
        children: [
          {
            path: ':submissionId',
            name: 'SubmissionDetail',
            component: SubmissionView,
          },
        ],
      },
      {
        path: 'publish',
        name: 'Publish',
        component: PublishView,
      },
      {
        path: 'cpq',
        name: 'CPQ',
        component: CPQSettingsView,
        meta: { requiresAuth: true, roles: ['admin'] },
      },
    ],
  },
  {
    path: '/funnel/:id',
    name: 'FormViewer',
    component: FormViewerPage,
    props: true,
    meta: { requiresAuth: false },
  },
  {
    path: '/oauth/calendar-callback',
    name: 'CalendarCallback',
    component: CalendarCallback,
    meta: { requiresAuth: false },
  },
  {
    path: '/oauth/google-callback',
    name: 'GoogleCallback',
    component: GoogleAuthCallback,
    meta: { requiresAuth: false },
  },
  {
    path: '/integrations',
    redirect: (to) => ({
      path: '/settings/integrations',
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: '/settings',
    component: SettingsLayout,
    meta: { requiresAuth: true, roles: ['admin', 'member'] },
    children: [
      {
        path: '',
        redirect: '/settings/my-account',
      },
      {
        path: 'integrations',
        name: 'SettingsIntegrations',
        component: SettingsIntegrationsView,
        meta: { roles: ['admin'] },
      },
      {
        path: 'suppliers',
        name: 'SettingsSuppliers',
        component: SettingsSuppliersView,
        meta: { roles: ['admin'] },
      },
      {
        path: 'resources',
        name: 'SettingsResources',
        component: () => import('@/pages/settings/SettingsResourcesView.vue'),
        meta: { roles: ['admin'] },
      },
      {
        path: 'suppliers/catalog',
        name: 'SettingsSupplierCatalog',
        component: () => import('@/pages/settings/SettingsSupplierCatalogView.vue'),
        meta: { roles: ['admin'] },
      },
      {
        path: 'pipelines',
        name: 'SettingsPipelines',
        component: () => import('@/pages/settings/SettingsPipelinesView.vue'),
        meta: { roles: ['admin'] },
      },
      {
        path: 'my-account',
        name: 'SettingsMyAccount',
        component: SettingsMyAccountView,
      },
      {
        path: 'notifications',
        name: 'SettingsNotifications',
        component: SettingsNotificationsView,
      },
      {
        path: 'organization',
        name: 'SettingsOrganization',
        component: SettingsOrganizationView,
        meta: { roles: ['admin'] },
      },
      {
        path: 'forecast',
        name: 'SettingsForecast',
        component: () => import('@/pages/settings/SettingsForecastView.vue'),
        meta: { roles: ['admin'] },
      },
      {
        path: 'billing',
        name: 'SettingsBilling',
        component: SettingsBillingView,
        meta: { roles: ['admin'] },
      },
      {
        path: 'invoicing',
        name: 'SettingsInvoicing',
        component: SettingsInvoicingView,
        meta: { roles: ['admin'] },
      },
      {
        path: 'billing/success',
        name: 'SettingsBillingSuccess',
        component: () => import('@/pages/settings/SettingsBillingSuccess.vue'),
        meta: { roles: ['admin'] },
      },
      {
        path: 'billing/cancel',
        name: 'SettingsBillingCancel',
        component: () => import('@/pages/settings/SettingsBillingCancel.vue'),
        meta: { roles: ['admin'] },
      },
    ],
  },
  {
    path: '/',
    component: DefaultLayout,
    meta: { requiresAuth: true, roles: ['admin', 'member', 'installer'] },
    beforeEnter: async (to) => {
      // Check for auth callback in URL
      if (window.location.hash) {
        const userStore = useUserStore();
        try {
          const verified = await userStore.init();
          if (!verified) return { path: '/login' };
          return { path: defaultAppPath() };
        } catch (error) {
          console.error('Error handling verification:', error);
          return { path: '/login' };
        }
      }
      return true;
    },
    children: [
      {
        path: '',
        name: 'Root',
        component: RootRedirect,
      },
      {
        path: 'home',
        name: 'Home',
        component: HomePage,
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'all-flows',
        name: 'AllFlows',
        component: AllFormsPage,
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'automations',
        name: 'Automations',
        component: AutomationsView,
        meta: { roles: ['admin'] },
      },
      {
        path: 'automations/:id',
        name: 'AutomationEditor',
        component: AutomationEditor,
        meta: { roles: ['admin'] },
      },
      {
        path: 'insights',
        name: 'Insights',
        component: InsightsPage,
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'stats',
        name: 'Stats',
        component: StatsPage,
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'deals',
        name: 'Deals',
        component: DealsPage,
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'deals/:id',
        name: 'DealDetail',
        component: DealsPage,
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'tasks',
        name: 'Tasks',
        component: TasksPage,
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'crm',
        name: 'CRM',
        component: CrmPage,
      },
      {
        path: 'crm/lead/:id',
        name: 'LeadDetail',
        component: LeadDetailPage,
        meta: { roles: ['admin', 'member'] },
      },
      {
        path: 'submissions',
        name: 'AllSubmissions',
        component: AllSubmissionsPage,
        meta: { roles: ['admin', 'member'] },
        children: [
          {
            path: ':submissionId',
            name: 'SubmissionDetailDirect',
            component: AllSubmissionsPage,
            meta: { roles: ['admin', 'member'] },
          },
        ],
      },
      {
        path: 'appointments',
        name: 'Appointments',
        component: AppointmentsPage,
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'proposals',
        name: 'Proposals',
        component: QuotesPage,
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'proposals/new',
        name: 'ProposalCreate',
        component: () => import('@/pages/dashboard/ProposalPage.vue'),
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'proposals/:id',
        name: 'ProposalDetail',
        component: () => import('@/pages/dashboard/ProposalPage.vue'),
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'projects',
        name: 'Projects',
        component: () => import('@/pages/dashboard/ProjectsPage.vue'),
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'projects/:id',
        name: 'ProjectDetail',
        component: () => import('@/pages/dashboard/ProjectDetailPage.vue'),
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'orders',
        name: 'Orders',
        component: () => import('@/pages/dashboard/OrdersPage.vue'),
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'orders/:id',
        name: 'OrderDetail',
        component: () => import('@/pages/dashboard/OrderDetailPage.vue'),
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'work-orders',
        name: 'WorkOrders',
        component: () => import('@/pages/dashboard/WorkOrdersPage.vue'),
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'work-orders/:id',
        name: 'WorkOrderDetail',
        component: () => import('@/pages/dashboard/WorkOrderDetailPage.vue'),
      },
      {
        path: 'invoices',
        name: 'Invoices',
        component: () => import('@/pages/dashboard/InvoicesPage.vue'),
        meta: { roles: ['admin', 'member'] },
      },
      {
        path: 'invoices/:id',
        name: 'InvoiceDetail',
        component: () => import('@/pages/dashboard/InvoiceDetailPage.vue'),
        meta: { roles: ['admin', 'member'] },
      },
      // Installer portal routes
      {
        path: 'vandaag',
        name: 'Vandaag',
        component: () => import('@/pages/dashboard/VandaagPage.vue'),
        meta: { roles: ['installer', 'admin', 'owner'] },
      },
      {
        path: 'mijn-week',
        name: 'MijnWeek',
        component: () => import('@/pages/dashboard/MijnWeekPage.vue'),
        meta: { roles: ['installer', 'admin', 'owner'] },
      },
      {
        path: 'geschiedenis',
        name: 'Geschiedenis',
        component: () => import('@/pages/dashboard/GeschiedenisPage.vue'),
        meta: { roles: ['installer', 'admin', 'owner'] },
      },
      {
        path: 'products',
        name: 'ProductFeeds',
        component: ProductFeedsView,
        meta: { roles: ['admin'] },
      },
      {
        path: 'products/catalog',
        name: 'ProductCatalogImport',
        component: () => import('@/pages/dashboard/CatalogImportPage.vue'),
        meta: { roles: ['super_admin'] },
      },
      {
        path: 'discounts',
        name: 'Discounts',
        component: () => import('@/pages/dashboard/DiscountsPage.vue'),
        meta: { roles: ['admin'] },
      },
      {
        path: 'products/:feedId',
        name: 'ProductFeedDetail',
        component: ProductFeedDetailPage,
        meta: { roles: ['admin'] },
      },
      {
        path: 'products/:feedId/:productIndex',
        name: 'ProductDetail',
        component: ProductDetailPage,
        meta: { roles: ['admin'] },
      },
      {
        path: 'support',
        name: 'Support',
        component: SupportPage,
      },
      {
        path: 'planning',
        name: 'Planning',
        component: PlanningPage,
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'planning/materieel',
        name: 'ResourceTimeline',
        component: () => import('@/pages/dashboard/ResourceTimelinePage.vue'),
        meta: { roles: ['admin', 'member', 'owner'] },
      },
      {
        path: 'servicemeldingen',
        name: 'ServiceNotifications',
        component: () => import('@/pages/dashboard/ServiceNotificationsPage.vue'),
        meta: { roles: ['admin', 'member', 'owner'] },
      },
    ],
  },
  {
    path: '/configurator/:id',
    name: 'ConfiguratorManagement',
    component: () => import('@/pages/dashboard/ConfiguratorManagementPage.vue'),
    meta: { requiresAuth: true, roles: ['admin'] },
  },
  {
    // Hidden Funnelflow team section. Lazy-loaded so the bundle never ships
    // to non-super-admins. The actual security boundary is on the backend
    // (`requirePlatformAdmin`); the route guard just prevents accidental
    // access in the browser.
    path: '/platform',
    component: () => import('@/layouts/PlatformLayout.vue'),
    meta: { requiresAuth: true, platformAdmin: true },
    children: [
      {
        path: '',
        name: 'PlatformLanding',
        component: () => import('@/pages/platform/PlatformLandingPage.vue'),
      },
      {
        path: 'organizations',
        name: 'PlatformOrganizations',
        component: () => import('@/pages/platform/PlatformOrganizationsPage.vue'),
      },
      {
        path: 'organizations/new',
        name: 'PlatformOrganizationNew',
        component: () => import('@/pages/platform/PlatformOrganizationNewPage.vue'),
      },
      {
        path: 'organizations/:id',
        name: 'PlatformOrganizationDetail',
        component: () => import('@/pages/platform/PlatformOrganizationDetailPage.vue'),
      },
      {
        path: 'users',
        name: 'PlatformUsers',
        component: () => import('@/pages/platform/PlatformUsersPage.vue'),
      },
      {
        path: 'billing/checkout-link',
        name: 'PlatformCheckoutLink',
        component: () => import('@/pages/platform/PlatformCheckoutLinkPage.vue'),
      },
      {
        path: 'billing/trial-extend',
        name: 'PlatformTrialExtend',
        component: () => import('@/pages/platform/PlatformTrialExtendPage.vue'),
      },
      {
        path: 'billing/manual-license',
        name: 'PlatformManualLicense',
        component: () => import('@/pages/platform/PlatformManualLicensePage.vue'),
      },
      {
        path: 'products/copy',
        name: 'PlatformProductsCopy',
        component: () => import('@/pages/platform/PlatformProductsCopyPage.vue'),
      },
      {
        path: 'catalog',
        name: 'PlatformCatalog',
        component: () => import('@/pages/platform/PlatformCatalogPage.vue'),
      },
      {
        path: 'catalog/sets',
        name: 'PlatformCatalogSets',
        component: () => import('@/pages/platform/PlatformCatalogSetsPage.vue'),
      },
      {
        path: 'catalog/:masterId',
        name: 'PlatformCatalogMaster',
        component: () => import('@/pages/dashboard/ProductDetailPage.vue'),
      },
      {
        path: 'demo/seed',
        name: 'PlatformDemoSeed',
        component: () => import('@/pages/platform/PlatformDemoSeedPage.vue'),
      },
      {
        path: 'audit',
        name: 'PlatformAudit',
        component: () => import('@/pages/platform/PlatformAuditPage.vue'),
      },
    ],
  },
  {
    path: '/uren-mockup',
    name: 'UrenMockup',
    component: () => import('@/pages/dashboard/UrenMockupPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy,
    meta: { requiresAuth: false },
  },
  {
    path: '/i/:token',
    name: 'PublicInvoice',
    component: () => import('@/pages/public/PublicInvoicePage.vue'),
    meta: { requiresAuth: false },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  try {
    // Add a timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Auth check timeout')), 15000);
    });

    const authCheckPromise = checkAuth(to, from, next);
    await Promise.race([authCheckPromise, timeoutPromise]);
  } catch (error) {
    console.error('Auth middleware error:', error);
    if (to.meta.requiresAuth === false) {
      // Allow navigation to public routes even if auth check fails
      next();
    } else {
      next('/login');
    }
  }
});

// Add error handling
router.onError((error) => {
  console.error('Router error:', error);
});

export default router;
